import React, { useState, useEffect, useRef, useId } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X, Upload, FileText, Clock, DollarSign, CheckCircle, Package, User, Briefcase } from "lucide-react";
import { Card } from "./card";
import { Button } from "./Button";
import { Badge } from "./badge";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { createOffer, updateOffer, fetchOffer } from "../../redux/Slices/offerSlice";
import { formatOfferForAPI } from "../../redux/Actions/OfferActions";
import { useUserJobs } from "../../hooks/useUserJobs";
import { getUserJobs } from "../../redux/Actions/AuthAction";
import { uploadContractFile } from "../../redux/Slices/contractSlice";
import { GlobalService, RoleEnum } from "../../modules";
import { useNavigate } from "react-router-dom";
import { getJobById } from "../../redux";
import { getDynamicCurrencySymbol } from "../../lib/currencyUtils";
import { 
  Offer, 
  OfferFile,
  OfferTimelineType,
  OfferStatus,
  OfferUser,
  OfferJob,
  OfferTimelineTypeEnum, 
  OfferStatusEnum 
} from "../../modules/shared/model/offer.interfaces";

// Re-export for backward compatibility
export type { 
  Offer,
  OfferFile,
  OfferTimelineType,
  OfferStatus,
  OfferUser,
  OfferJob
};
export { OfferTimelineTypeEnum, OfferStatusEnum };

interface RequestQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  isLocal?: boolean;
  professional: any;
  isProfessional?: boolean;
  offerId?: string; // For editing existing offers
  existingOffer?: Partial<Offer>; // Direct offer data for editing
  userJobs?: any[]; // Jobs created by current user
  onOfferCreated?: (offer: Offer) => void;
  onOfferUpdated?: (offer: Offer) => void;
  // If provided, only show jobs from these offer references.
  // Accepts either offer IDs (string) or direct job refs like { job: { id: string } }
  restrictOfferIds?: Array<string | { job?: { id?: string } }>;
  selectedJobId?: string; // Prefill job id if provided
  isSentDefault?: boolean; // Control isSent on creation
}

const RequestQuoteModal: React.FC<RequestQuoteModalProps> = ({
  isOpen,
  onClose,
  isLocal = false,
  professional,
  isProfessional = false,
  offerId,
  existingOffer,
  userJobs = [],
  onOfferCreated,
  onOfferUpdated,
  restrictOfferIds = [],
  selectedJobId,
  isSentDefault = true
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  console.log("isProfessional", isProfessional);
  const { loading: isSubmitting, error } = useAppSelector((state: any) => state.offers);
  const { user } = useAppSelector((state: any) => state.auth);
  const { jobs: userJobsList, loading: jobsLoading, error: jobsError } = useUserJobs();
  const uploadInputId = useId();

  // State for jobs fetched from offer's job user ID
  const [jobsFromOfferUser, setJobsFromOfferUser] = useState<any[]>([]);
  const [jobsFromOfferUserLoading, setJobsFromOfferUserLoading] = useState(false);
  const [jobsFromOfferUserError, setJobsFromOfferUserError] = useState<string | null>(null);
  const [restrictedJobs, setRestrictedJobs] = useState<any[]>([]);
  const [formData, setFormData] = useState<Partial<Offer>>({
    timelineType: { id: OfferTimelineTypeEnum.Day },
    status: { id: OfferStatusEnum.Approved },
    files: [],
    isWarranty: false,
    isPartsIncluded: false,
    timeline: 1,
    description: "",
    title: "",
    amount: null,
    user: { id: professional?.id || "" },
    job: { id: "" }
  });
  const [selectedJobDetails, setSelectedJobDetails] = useState<any>(null);

  const navigate = useNavigate();

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string, name: string, url: string, type: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFileName, setUploadingFileName] = useState("");

  // Timeline type options
  const timelineTypeOptions = [
    { id: OfferTimelineTypeEnum.Hour, name: t("requestQuoteModal.timelineTypes.hours") },
    { id: OfferTimelineTypeEnum.Day, name: t("requestQuoteModal.timelineTypes.days") },
    { id: OfferTimelineTypeEnum.Week, name: t("requestQuoteModal.timelineTypes.weeks") }
  ];

  // Validation function
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title || formData.title.length < 10) {
      newErrors.title = t("requestQuoteModal.validation.titleMinLength");
    }
    if (formData.title && formData.title.length > 70) {
      newErrors.title = t("requestQuoteModal.validation.titleMaxLength");
    }

    if (!formData.description || formData.description.length < 15) {
      newErrors.description = t("requestQuoteModal.validation.descriptionMinLength");
    }
    if (formData.description && formData.description.length > 600) {
      newErrors.description = t("requestQuoteModal.validation.descriptionMaxLength");
    }

    if (!formData.job?.id) {
      if (availableJobs.length === 0) {
        newErrors.job = t("requestQuoteModal.validation.noJobsAvailable");
      } else {
        newErrors.job = t("requestQuoteModal.validation.selectJob");
      }
    }

    if (!formData.timeline || formData.timeline <= 0) {
      newErrors.timeline = t("requestQuoteModal.validation.timelineGreaterThanZero");
    }

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = t("requestQuoteModal.validation.amountGreaterThanZero");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      console.log("currentOffer", Number(currentOffer?.status?.id));
      const offerData = {
        ...formData,
        status: { id: (Number(currentOffer?.status?.id) && Number(currentOffer?.status?.id) != OfferStatusEnum.Admin_Suspended) ? Number(currentOffer?.status?.id) : OfferStatusEnum.Approved } // Always set to Approved for new offers
      };

      if (offerId) {
        // Update existing offer
        const result = await dispatch(updateOffer({ id: offerId, data: formatOfferForAPI(offerData) })).unwrap();
        onOfferUpdated?.(result);
      } else {
        // Create new offer
        const result = await dispatch(createOffer({ ...formatOfferForAPI(offerData), isSent: Boolean(isSentDefault) })).unwrap();
        onOfferCreated?.(result);
      }

      onClose();
    } catch (error) {
    }
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Start progress immediately
    setIsUploading(true);
    setUploadProgress(5);
    setUploadingFileName(t("requestQuoteModal.upload.startingUpload"));

    try {
      const newUploadedFiles: Array<{ id: string, name: string, url: string, type: string }> = [];

      for (let i = 0; i < Array.from(files).length; i++) {
        const file = Array.from(files)[i];
        setUploadingFileName(file.name);

        // Realistic progress simulation for each file
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const fileProgress = ((i + 1) / Array.from(files).length) * 100;
            const targetProgress = Math.min(prev + 4, fileProgress - 15);
            if (prev >= fileProgress - 15) {
              clearInterval(progressInterval);
              return prev;
            }
            return targetProgress;
          });
        }, 180);

        const { payload } = await dispatch(uploadContractFile(file) as any);
        const fileId = payload?.file?.id;
        const fileUrl = payload?.file?.path;

        if (fileId) {
          newUploadedFiles.push({
            id: fileId,
            name: file.name,
            url: fileUrl || URL.createObjectURL(file),
            type: file.type,
          });
        }

        // Update progress for this file
        const fileProgress = ((i + 1) / Array.from(files).length) * 100;
        setUploadProgress(fileProgress);
      }

      // Complete the progress and keep it visible briefly
      setUploadProgress(100);
      setUploadingFileName(t("requestQuoteModal.upload.uploadCompleted"));

      setTimeout(() => {
        setUploadProgress(0);
        setUploadingFileName("");
        setIsUploading(false);
      }, 800);

      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

      // Update the form data files array with file IDs
      setFormData(prev => ({
        ...prev,
        files: [
          ...(prev.files || []),
          ...newUploadedFiles.map(file => ({ id: file.id }))
        ],
      }));

      GlobalService.showToast("success", t("requestQuoteModal.upload.success"), t("requestQuoteModal.upload.filesUploadedSuccessfully", { count: newUploadedFiles.length }));
    } catch (error) {
      GlobalService.showToast("error", t("requestQuoteModal.upload.uploadFailed"), t("requestQuoteModal.upload.uploadFailedMessage"));
      setUploadProgress(0);
      setUploadingFileName("");
      setIsUploading(false);
    }
  };

  // Remove file
  const handleRemoveUploadedFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));

    // Remove from form data files array
    setFormData(prev => ({
      ...prev,
      files: prev.files?.filter(file => file.id !== fileId) || []
    }));
  };

  // Get current offer from store for editing
  const currentOffer = useAppSelector((state: any) => state.offers.currentOffer);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {

      if (offerId) {
        // Fetch existing offer data and populate form
        dispatch(fetchOffer(offerId));
      } else {
        // Reset form for new offer
        setFormData({
          timelineType: { id: OfferTimelineTypeEnum.Day },
          status: { id: OfferStatusEnum.Approved },
          files: [],
          isWarranty: false,
          isPartsIncluded: false,
          timeline: 1,
          description: "",
          title: "",
          amount: null,
          user: { id: (selectedJobId ? (user?.id || "") : (professional?.id || "")) },
          job: { id: selectedJobId || "" }
        });
        setUploadedFiles([]);
        setIsUploading(false);
        setUploadProgress(0);
        setUploadingFileName("");
        setErrors({});
      }
    }
  }, [isOpen]);

  // If a selectedJobId is provided, fetch job details and populate
  useEffect(() => {
    const doFetch = async () => {
      if (!isOpen || !selectedJobId) return;
      try {
        const res: any = await dispatch(getJobById({ jobId: selectedJobId, isShowLoader: false }));
        if (res?.meta?.requestStatus === "fulfilled") {
          const job = res.payload?.data || res.payload;
          const title = job?.title || job?.description || "";
          const description = job?.description || job?.title || "";
          setSelectedJobDetails({ id: job?.id || selectedJobId, title, description });
          setFormData(prev => ({
            ...prev,
            user: { id: user?.id || prev.user?.id || "" },
            job: { id: selectedJobId },
            title: prev.title && prev.title.length > 0 ? prev.title : title,
            description: prev.description && prev.description.length > 0 ? prev.description : description,
            amount: prev.amount && prev.amount > 0 ? prev.amount : (job?.amount || job?.budget || null)
          }));
        }
      } catch { }
    };
    doFetch();
  }, [isOpen, selectedJobId, dispatch]);

  // Populate form when editing existing offer
  useEffect(() => {
    // if (existingOffer) {


    //   // Use existingOffer prop if provided (highest priority)
    //   setFormData({
    //     timelineType: { id: existingOffer.timelineType?.id || OfferTimelineTypeEnum.Day },
    //     status: { id: existingOffer.status?.id || OfferStatusEnum.Submitted },
    //     files: existingOffer.files || [],
    //     isWarranty: existingOffer.isWarranty || false,
    //     isPartsIncluded: existingOffer.isPartsIncluded || false,
    //     timeline: existingOffer.timeline || 1,
    //     description: existingOffer.description || "",
    //     title: existingOffer.title || "",
    //     user: { id: existingOffer.user?.id || professional?.id || "" },
    //     job: { id: existingOffer.job?.id || "" }
    //   });

    //   // Initialize uploaded files from existing offer data
    //   if (existingOffer.files && existingOffer.files.length > 0) {
    //     const existingFiles = existingOffer.files.map((file: any) => ({
    //       id: file.id,
    //       name: file.name || `File ${file.id}`,
    //       url: file.path || file.url || "",
    //       type: file.type || "application/octet-stream"
    //     }));
    //     setUploadedFiles(existingFiles);
    //   }
    // } else 
    if (currentOffer && offerId) {
      // Fallback to Redux store data
      setFormData({
        timelineType: { id: Number(currentOffer.timelineType?.id) || OfferTimelineTypeEnum.Day },
        status: { id: Number(currentOffer.status?.id) },
        files: currentOffer.files || [],
        isWarranty: currentOffer.isWarranty || false,
        isPartsIncluded: currentOffer.isPartsIncluded || false,
        timeline: currentOffer.timeline || 1,
        description: currentOffer.description || "",
        title: currentOffer.title || "",
        amount: currentOffer.amount || null,
        user: { id: currentOffer.user?.id || professional?.id || "" },
        job: { id: currentOffer.job?.id || "" }
      });

      // Initialize uploaded files from existing offer data
      if (currentOffer.files && currentOffer.files.length > 0) {
        const existingFiles = currentOffer.files.map((file: any) => ({
          id: file.id,
          name: file.name || `File ${file.id}`,
          url: file.path || file.url || "",
          type: file.type || "application/octet-stream"
        }));
        setUploadedFiles(existingFiles);
      }

      // Fetch jobs from the offer's job user ID if available
      if (currentOffer.job?.userId?.id) {
        fetchJobsFromOfferUser(currentOffer.job.userId.id);
      }
    }
    if (professional && professional.id && professional.id !== user?.id && Number(professional.role?.id) === RoleEnum.client) {
      fetchJobsFromOfferUser(professional.id);
    }
  }, [existingOffer, currentOffer, offerId, professional]);

  // Function to fetch jobs from offer's job user ID using getUserJobs
  const fetchJobsFromOfferUser = async (userId: string) => {
    if (!userId) return;

    setJobsFromOfferUserLoading(true);
    setJobsFromOfferUserError(null);

    try {
      const response = await dispatch(getUserJobs({
        userId: userId,
        page: 1,
        limit: 10,
        filters: { "status": [6, 5, 1, 2, 7, 8] },
        sort: [{ orderBy: "createdAt", order: "DESC" }] // Sort by creation date descending (latest first)
      }));

      if (response.meta.requestStatus === "fulfilled") {
        const jobsData = response.payload.data || response.payload;
        setJobsFromOfferUser(Array.isArray(jobsData) ? jobsData : []);
      } else {
        setJobsFromOfferUserError("Failed to load customer jobs");
      }
    } catch (error) {
      setJobsFromOfferUserError("Failed to fetch customer jobs");
    } finally {
      setJobsFromOfferUserLoading(false);
    }
  };

  // Use userJobs prop as fallback if hook doesn't have data yet
  // When editing an offer, prioritize jobs from the offer's job user ID
  const availableJobs = (restrictedJobs.length > 0
    ? restrictedJobs
    : (jobsFromOfferUser.length > 0
      ? jobsFromOfferUser
      : (userJobsList.length > 0
        ? userJobsList
        : userJobs)));

  // If restrictOfferIds is provided (for professionals), derive jobs and restrict to those
  useEffect(() => {
    const fetchRestrictedJobs = async () => {
      if (!restrictOfferIds || restrictOfferIds.length === 0) {
        setRestrictedJobs([]);
        return;
      }
      // Normalize to a stable key of job ids
      const normalizedJobIds = Array.from(new Set((restrictOfferIds || []).map((r: any) => (typeof r === "object" && r?.job?.id ? String(r.job.id) : String(r)))));
      const key = JSON.stringify(normalizedJobIds.sort());
      const lastKeyRef = (fetchRestrictedJobs as any).lastKeyRef || ((fetchRestrictedJobs as any).lastKeyRef = { current: "" });
      if (lastKeyRef.current === key) {
        return;
      }
      try {
        const fetched: any[] = [];
        for (const entry of restrictOfferIds as any[]) {
          // If entry is an object with job.id, use it directly
          const directJobId = (typeof entry === "object" && entry?.job?.id) ? String(entry.job.id) : null;
          if (directJobId) {
            fetched.push({ id: directJobId });
            continue;
          }
          // Otherwise treat as offer id and resolve job from offer
          const offerIdStr = String(entry);
          const res: any = await dispatch(fetchOffer(offerIdStr));
          if (res?.meta?.requestStatus === "fulfilled") {
            const off = res.payload;
            if (off?.job?.id) {
              fetched.push({ id: off.job.id, title: off.job.title || off.job.description || `Job #${off.job.id}`, description: off.job.description || off.job.title || "" });
            }
          }
        }
        const unique = Array.from(new Map(fetched.map(j => [j.id, j])).values());
        setRestrictedJobs(unique);
        lastKeyRef.current = key;
      } catch (e) {
        setRestrictedJobs([]);
      }
    };
    fetchRestrictedJobs();
  }, [dispatch, restrictOfferIds && restrictOfferIds.length ? JSON.stringify((restrictOfferIds as any[]).map((r: any) => (typeof r === "object" && r?.job?.id ? String(r.job.id) : String(r))).sort()) : ""]);

  if (!isOpen) return null;

  // Check if user is authenticated
  if (!user?.id) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            className="bg-white rounded-xl w-full max-w-md p-6 text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("requestQuoteModal.authRequired.title")}
            </h3>
            <p className="text-gray-600 mb-4">
              {t("requestQuoteModal.authRequired.message")}
            </p>
            <Button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {t("requestQuoteModal.close")}
            </Button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {offerId ? t("requestQuoteModal.header.editQuoteRequest") : (isProfessional ? t("requestQuoteModal.header.sendQuote") : t("requestQuoteModal.header.requestQuote"))}
              </h2>
              <p className="text-gray-600 mt-1">
                {professional?.name
                  ? `${isProfessional ? t("requestQuoteModal.header.sendQuoteTo") : t("requestQuoteModal.header.sendQuoteRequestTo")} ${professional.name}`
                  : `${t("requestQuoteModal.header.createNew")} ${isProfessional ? t("requestQuoteModal.header.quote") : t("requestQuoteModal.header.quoteRequest")}`
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* No Jobs Warning */}
            {!selectedJobId && !jobsLoading && !jobsFromOfferUserLoading && !jobsError && !jobsFromOfferUserError && availableJobs.length === 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                    <span className="text-white text-xs font-bold">!</span>
                  </div>
                  <p className="text-amber-800 text-sm font-medium">
                    {offerId ? t("requestQuoteModal.noJobsWarning.noJobsFoundForCustomer") : t("requestQuoteModal.noJobsWarning.noJobsAvailable")}
                  </p>
                </div>
                <p className="text-amber-700 text-sm mt-1 ml-7">
                  {offerId
                    ? t("requestQuoteModal.noJobsWarning.customerNoJobsForEditing")
                    : t("requestQuoteModal.noJobsWarning.createJobFirst")
                  }
                </p>
                <p className="text-red-700 text-sm mt-1 ml-7">
                  {!offerId && t("requestQuoteModal.noJobsWarning.letProfessionalKnow")}
                </p>
                {!offerId && !isProfessional && (
                  <div className="mt-3 ml-7">
                    <Button
                      type="button"
                      variant="outline"
                      className="bg-amber-600 text-black border-amber-600 hover:bg-amber-700 hover:border-amber-700"
                      onClick={() => {
                        if (isLocal) {
                          navigate("/clients/postajob");
                        } else {
                          navigate("/clients/postajob/international");
                        }
                      }}
                    >
                      {t("requestQuoteModal.createNewJob")}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Professional Info */}
            <Card className="p-4 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {isProfessional ? t("requestQuoteModal.professionalInfo.quote") : t("requestQuoteModal.professionalInfo.quoteRequest")}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {professional?.title || `${isProfessional ? t("requestQuoteModal.professionalInfo.sendingQuoteTo") : t("requestQuoteModal.professionalInfo.requestingQuoteFrom")} ${user?.name || t("requestQuoteModal.professionalInfo.professional")}`}
                  </p>
                </div>
              </div>
            </Card>

            {/* Job Selection */}
            <div>
              {selectedJobId ? (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t("requestQuoteModal.jobSelection.job")}</label>
                  <Card className="p-3">
                    <div className="text-sm font-medium text-gray-900">{selectedJobDetails?.title || t("requestQuoteModal.jobSelection.selectedJob")}</div>
                  </Card>
                </div>
              ) : (
                <>
                  <div className="flex flex-row justify-between items-center gap-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t("requestQuoteModal.jobSelection.selectJob")}</label>
                    {!isProfessional && <button className="text-blue-500 text-sm" onClick={() => {
                      if (isLocal) {
                        navigate("/clients/postajob");
                      } else {
                        navigate("/clients/postajob/international");
                      }
                    }}>{t("requestQuoteModal.createNewJob")}</button>}
                  </div>

                  <select
                    value={formData.job?.id}
                    onChange={(e) => {
                      const selectedJobIdLocal = e.target.value;
                      const source = restrictedJobs.length > 0 ? restrictedJobs : availableJobs;
                      const selectedJob = source.find(j => j.id === selectedJobIdLocal);

                      setFormData(prev => ({
                        ...prev,
                        job: {
                          id: selectedJobIdLocal,
                          title: selectedJob?.title || ""
                        },
                        // Auto-populate title, description, and amount from selected job
                        title: selectedJob?.title || prev.title || "",
                        description: selectedJob?.description || prev.description || "",
                        amount: selectedJob?.amount || selectedJob?.budget || prev.amount || null
                      }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.job ? "border-red-500" : "border-gray-300"
                      }`}
                    disabled={jobsLoading || jobsFromOfferUserLoading}
                  >
                    <option value="">
                      {jobsLoading || jobsFromOfferUserLoading ? t("requestQuoteModal.jobSelection.loadingJobs") : t("requestQuoteModal.jobSelection.selectJobOption")}
                    </option>
                    {(restrictedJobs.length > 0 ? restrictedJobs : availableJobs).length > 0 ? (
                      (restrictedJobs.length > 0 ? restrictedJobs : availableJobs).map((job) => (
                        <option key={job.id} value={job.id}>
                          {job.title || job.description || `Job #${job.id}`}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        {jobsError || jobsFromOfferUserError ? t("requestQuoteModal.jobSelection.errorLoadingJobs") : t("requestQuoteModal.jobSelection.noJobsAvailable")}
                      </option>
                    )}
                  </select>
                  {errors.job && (
                    <p className="text-red-500 text-sm mt-1">{errors.job}</p>
                  )}
                  {(jobsError || jobsFromOfferUserError) && (
                    <p className="text-red-500 text-sm mt-1">{jobsError || jobsFromOfferUserError}</p>
                  )}
                  {(jobsLoading || jobsFromOfferUserLoading) && (
                    <p className="text-blue-500 text-sm mt-1">
                      {offerId ? t("requestQuoteModal.jobSelection.loadingJobsFromCustomer") : t("requestQuoteModal.jobSelection.loadingYourJobs")}
                    </p>
                  )}
                  {!jobsLoading && !jobsFromOfferUserLoading && !jobsError && !jobsFromOfferUserError && availableJobs.length === 0 && (
                    <p className="text-amber-600 text-sm mt-1">
                      {offerId
                        ? t("requestQuoteModal.jobSelection.noJobsFoundForCustomer")
                        : t("requestQuoteModal.jobSelection.noJobsAvailableCreateFirst")
                      }
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("requestQuoteModal.formFields.projectTitle")}
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.title ? "border-red-500" : "border-gray-300"
                  }`}
                placeholder={t("requestQuoteModal.formFields.projectTitlePlaceholder")}
                maxLength={70}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                {formData.title?.length || 0}/70 {t("requestQuoteModal.formFields.characters")}
              </p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("requestQuoteModal.formFields.projectDescription")}
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.description ? "border-red-500" : "border-gray-300"
                  }`}
                rows={4}
                placeholder={t("requestQuoteModal.formFields.projectDescriptionPlaceholder")}
                maxLength={600}
              />
              {errors.description && (
                <p className="text-red-500 text-sm mt-1">{errors.description}</p>
              )}
              <p className="text-gray-500 text-sm mt-1">
                {formData.description?.length || 0}/600 {t("requestQuoteModal.formFields.characters")}
              </p>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("requestQuoteModal.formFields.projectAmount")}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-lg font-bold text-gray-400">{getDynamicCurrencySymbol()}</span>
                </div>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || null }))}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.amount ? "border-red-500" : "border-gray-300"
                    }`}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>
              {errors.amount && (
                <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
              )}
            </div>



            {/* Timeline */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("requestQuoteModal.formFields.timeline")}
                </label>
                <input
                  type="number"
                  value={formData.timeline}
                  onChange={(e) => setFormData(prev => ({ ...prev, timeline: parseInt(e.target.value) || 0 }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.timeline ? "border-red-500" : "border-gray-300"
                    }`}
                  min="1"
                  placeholder="1"
                />
                {errors.timeline && (
                  <p className="text-red-500 text-sm mt-1">{errors.timeline}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("requestQuoteModal.formFields.timelineType")}
                </label>
                <select
                  value={formData.timelineType?.id}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    timelineType: { id: parseInt(e.target.value) as OfferTimelineTypeEnum }
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {timelineTypeOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Additional Options */}
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isWarranty}
                  onChange={(e) => setFormData(prev => ({ ...prev, isWarranty: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{t("requestQuoteModal.formFields.includeWarranty")}</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isPartsIncluded}
                  onChange={(e) => setFormData(prev => ({ ...prev, isPartsIncluded: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{t("requestQuoteModal.formFields.partsIncluded")}</span>
              </label>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("requestQuoteModal.fileUpload.attachDocuments")}
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id={uploadInputId}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={isUploading}
                />
                <label htmlFor={uploadInputId} className={`cursor-pointer ${isUploading ? "opacity-50" : ""}`}>
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">{t("requestQuoteModal.fileUpload.uploading")}</p>
                      {uploadingFileName && (
                        <p className="text-xs text-gray-500 mt-1">{uploadingFileName}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        {t("requestQuoteModal.fileUpload.clickToUpload")}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("requestQuoteModal.fileUpload.supportedFormats")}
                      </p>
                    </>
                  )}
                </label>
              </div>

              {/* Upload Progress Bar */}
              {isUploading && (
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      {uploadingFileName ? t("requestQuoteModal.fileUpload.uploadingFile", { fileName: uploadingFileName }) : t("requestQuoteModal.fileUpload.uploading")}
                    </span>
                    <span className="text-gray-500">{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({file.type})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveUploadedFile(file.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                {t("requestQuoteModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={isSubmitting || !(formData.job?.id) || !(formData.amount && formData.amount > 0)}
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {offerId ? t("requestQuoteModal.buttons.updating") : (isProfessional ? t("requestQuoteModal.buttons.sending") : t("requestQuoteModal.buttons.submitting"))}
                  </div>
                ) : (
                  offerId ? `${t("requestQuoteModal.buttons.update")} ${isProfessional ? t("requestQuoteModal.buttons.quote") : t("requestQuoteModal.buttons.quoteRequest")}` : (selectedJobId ? t("requestQuoteModal.buttons.startChat") : `${isProfessional ? t("requestQuoteModal.buttons.sendQuote") : t("requestQuoteModal.buttons.submitQuoteRequest")}`)
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RequestQuoteModal;
